<template>
  <transition name="left-fade">
    <div v-if="activeMetric?.key" ref="tooltipElem" class="label-tooltip">
      <div class="label-tooltip-content p-1 flex flex-col gap-3">
        <div class="p-2">
          <BaseText type="label" size="sm" class="text-white">
            {{ activeMetric.name }}
          </BaseText>
          <BaseText type="body" size="sm" class="text-white opacity-70">
            {{ activeMetric.description }}
          </BaseText>
        </div>
        <div v-if="activeMetric?.formula" class="bg-neutral-alpha-50 rounded-md p-1 uppercase">
          <div class="flex items-center gap-1 px-1 py-0.5">
            <FormulaIcon class="text-white" alpha="0.68" />
            <BaseText type="heading" size="overline" class="text-white">
              Formula
            </BaseText>
          </div>
          <div>
            <MetricFormulaRenderer :ast="activeMetric.formula" />
          </div>
        </div>
        <div v-if="activeMetric?.note" class="text-secondary-yellow-25 tooltip-note p-1 space-y-1">
          <div class="flex items-center gap-1">
            <NoteIcon />
            <BaseText type="heading" size="overline">
              Note
            </BaseText>
          </div>
          <div class="tooltip-note-body p-2 rounded-md">
            <BaseText size="sm" class="whitespace-pre-wrap break-words">{{ activeMetric.note }}</BaseText>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { animate, easeOut } from 'popmotion'
import _ from 'lodash'

// Components
import MetricFormulaRenderer from '../MetricFormulaRenderer.vue'

// Icons
import FormulaIcon from '../../globals/Icons/LensIcons/MetricIcons/FormulaIcon.vue'
import NoteIcon from '../../globals/Icons/LensIcons/MetricIcons/NoteIcon.vue'

export default {
  name: 'MetricHoverTooltip',
  components: {
    MetricFormulaRenderer,
    FormulaIcon,
    NoteIcon
  },
  props: {
    // The metric currently under the cursor, or null once the cursor leaves it
    hoveredMetric: {
      type: Object,
      default: () => null
    },
    // Prefix of the DOM id used to anchor the tooltip against its metric row
    idPrefix: {
      type: String,
      default: 'metric-'
    }
  },
  data () {
    return {
      activeMetric: null,
      lastHover: {
        timestamp: null,
        elem: null
      },
      debounceUnHover: null
    }
  },
  watch: {
    // Keeps the tooltip alive while the cursor travels between adjacent rows
    hoveredMetric (metric) {
      if (!metric) {
        this.lastHover = {
          timestamp: Date.now(),
          elem: metric
        }
        this.debounceUnHover()
        return
      }
      this.activeMetric = metric
      this.lastHover.elem = metric
    },
    // Handles tooltip tweening
    activeMetric (newElem, oldElem) {
      if (newElem) {
        this.$nextTick(() => {
          const hoveredElement = document.querySelector(this.metricSelector(newElem.key))
          const parentElement = hoveredElement.parentElement
          const clientRect = hoveredElement.getBoundingClientRect()
          const parentRect = parentElement.getBoundingClientRect()
          const tooltip = this.$refs.tooltipElem
          const halfTooltipHeight = clientRect.height / 2
          const newTop = clientRect.top - 80 - halfTooltipHeight

          // Handle init
          if (!oldElem) {
            tooltip.style.left = `${parentRect.right}px`
            tooltip.style.top = `${newTop}px`
            return
          }
          const prevHoveredElement = document.querySelector(this.metricSelector(oldElem.key))
          const oldClientRect = prevHoveredElement.getBoundingClientRect()

          // Account for 80px drawer padding
          const oldTop = oldClientRect.top - 80 - halfTooltipHeight
          animate({
            from: oldTop,
            to: newTop,
            ease: easeOut,
            duration: 100,
            onUpdate: (value) => {
              tooltip.style.top = `${value}px`
            }
          })
          tooltip.style.left = `${parentRect.right}px`
        })
      }
    }
  },
  mounted () {
    this.debounceUnHover = _.debounce(this.resetLastHover, 100)
  },
  methods: {
    // Metric keys can contain dots (e.g. custom conversions), which must be escaped in a selector
    metricSelector (key) {
      return `#${this.idPrefix}${key.replace(/\./g, '\\.')}`
    },
    resetLastHover () {
      if (!this.lastHover.elem) {
        this.activeMetric = null
      }
    }
  }
}
</script>

<style scoped>
.left-fade-enter-active,
.left-fade-leave-active {
  transition: opacity 100ms;
}

.left-fade-enter-from, .left-fade-enter, .left-fade-leave-to {
  opacity: 0;
}
.left-fade-enter-to, .left-fade-leave-from {
  opacity: 1;
}

.label-tooltip {
  position: fixed;
  margin-top: auto;
  margin-bottom: auto;
  width: 290px;
  z-index: 9999;
}
.label-tooltip-content{
    background: rgba(6, 7, 15, 0.92);
    border-radius: 12px;
    backdrop-filter: blur(10px);
}

.tooltip-note{
  border-radius: 6px;
  background: rgba(255,190,76,0.08);
}
.tooltip-note-body{
  background: rgba(255,190,76,0.08);
}
</style>
